import { useEffect, useMemo, useRef, useState } from 'react';
import { BrandLoader } from '../BrandLogo';

export type ClinicConsultation = {
  id: string;
  studentId?: string;
  studentName: string;
  doctorId?: string;
  doctorName?: string;
  question: string;
  answer: string | null;
  status: string;
  responseDeadline: string;
  answeredAt: string | null;
  createdAt: string;
};

export type ClinicChatDoctor = {
  id: string;
  fullName: string;
  specialty?: string | null;
  photoUrl?: string | null;
  acceptingConsultations?: boolean;
  isActive?: boolean;
};

type Thread = {
  key: string;
  studentName: string;
  doctorName: string;
  doctorId: string;
  items: ClinicConsultation[];
  pendingCount: number;
  lastAt: string;
  preview: string;
  unread: boolean;
};

const DOCTOR_COLORS = ['#EC7C23', '#059669', '#24255F', '#7C3AED', '#DB2777', '#0284C7'];

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]!.charAt(0)}${parts[1]!.charAt(0)}`.toUpperCase();
}

function shortName(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return name;
  return `${parts[0]} ${parts[1]!.charAt(0)}.`;
}

function fmtTime(iso: string) {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function threadKey(c: ClinicConsultation) {
  const student = c.studentId || c.studentName || c.id;
  const doctor = c.doctorId || c.doctorName || 'doctor';
  return `${student}::${doctor}`;
}

function doctorColor(index: number) {
  return DOCTOR_COLORS[index % DOCTOR_COLORS.length]!;
}

function DoctorAvatar({
  name,
  photoUrl,
  color,
  className,
}: {
  name: string;
  photoUrl?: string | null;
  color: string;
  className: string;
}) {
  return (
    <span className={className} style={photoUrl ? undefined : { background: color }}>
      {photoUrl ? <img src={photoUrl} alt="" /> : initials(name)}
    </span>
  );
}

function doctorRole(d: ClinicChatDoctor, index: number) {
  if (d.specialty?.trim()) return d.specialty.trim();
  if (index === 0) return 'Clinic Director';
  return 'Mentor';
}

export function buildConsultationThreads(consultations: ClinicConsultation[]): Thread[] {
  const map = new Map<string, ClinicConsultation[]>();
  for (const c of consultations) {
    const key = threadKey(c);
    const list = map.get(key) ?? [];
    list.push(c);
    map.set(key, list);
  }
  const threads: Thread[] = [];
  for (const [key, items] of map) {
    const sorted = [...items].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    const last = sorted[sorted.length - 1]!;
    const pendingItems = sorted.filter(c => c.status === 'pending' && !c.answer);
    const pendingCount = pendingItems.length;
    const latestPending = pendingItems[pendingItems.length - 1] ?? null;
    threads.push({
      key,
      studentName: last.studentName,
      doctorName: last.doctorName || 'Mentor',
      doctorId: last.doctorId || '',
      items: sorted,
      pendingCount,
      lastAt: last.answeredAt ?? last.createdAt,
      preview: latestPending?.question ?? last.answer ?? last.question,
      unread: pendingCount > 0,
    });
  }
  return threads.sort((a, b) => {
    if (a.unread !== b.unread) return a.unread ? -1 : 1;
    return new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime();
  });
}

export function ClinicChatPanel({
  consultations,
  doctors,
  selectedDoctorId,
  onSelectDoctor,
  loading,
  activeKey,
  onSelect,
  onSubmitAnswer,
  submitting,
  stacked = false,
}: {
  consultations: ClinicConsultation[];
  doctors: ClinicChatDoctor[];
  selectedDoctorId: string | null;
  onSelectDoctor: (doctorId: string) => void;
  loading: boolean;
  activeKey: string | null;
  onSelect: (key: string | null) => void;
  onSubmitAnswer: (consultationId: string, answer: string) => void | Promise<void>;
  submitting: boolean;
  stacked?: boolean;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [acctOpen, setAcctOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  const doctorList = useMemo(() => {
    if (doctors.length > 0) return doctors;
    const fromConvos = new Map<string, ClinicChatDoctor>();
    for (const c of consultations) {
      if (!c.doctorId) continue;
      if (!fromConvos.has(c.doctorId)) {
        fromConvos.set(c.doctorId, {
          id: c.doctorId,
          fullName: c.doctorName || 'Mentor',
        });
      }
    }
    return Array.from(fromConvos.values());
  }, [doctors, consultations]);

  useEffect(() => {
    if (!acctOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!switcherRef.current?.contains(e.target as Node)) setAcctOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAcctOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [acctOpen]);

  const selectedDoctor = doctorList.find(d => d.id === selectedDoctorId) ?? doctorList[0] ?? null;
  const selectedIndex = selectedDoctor
    ? Math.max(0, doctorList.findIndex(d => d.id === selectedDoctor.id))
    : 0;

  const filteredConsultations = useMemo(() => {
    if (!selectedDoctor) return consultations;
    return consultations.filter(c => c.doctorId === selectedDoctor.id);
  }, [consultations, selectedDoctor]);

  const threads = buildConsultationThreads(filteredConsultations);
  const active = stacked
    ? threads.find(t => t.key === activeKey) ?? null
    : threads.find(t => t.key === activeKey) ?? threads[0] ?? null;
  const threadOpen = Boolean(stacked && active);
  const unread = threads.filter(t => t.unread).length;

  const setDraft = (id: string, value: string) => {
    setDrafts(prev => ({ ...prev, [id]: value }));
  };

  const submit = async (consultationId: string) => {
    const text = (drafts[consultationId] ?? '').trim();
    if (!text || submitting) return;
    await onSubmitAnswer(consultationId, text);
    setDrafts(prev => {
      const next = { ...prev };
      delete next[consultationId];
      return next;
    });
  };

  return (
    <aside
      className={[
        'cd-chat-panel',
        stacked ? 'cd-chat-panel--stacked' : '',
        threadOpen ? 'is-thread' : '',
      ].filter(Boolean).join(' ')}
      aria-label="Student messages"
    >
      {!(stacked && threadOpen) && (
      <div className="cd-chat-hd">
        <div className="cd-chat-hd-title">Student Messages</div>

        {selectedDoctor ? (
          <div className="cd-acct-switcher" ref={switcherRef}>
            <button
              type="button"
              className="cd-acct-switcher-btn"
              onClick={() => setAcctOpen(o => !o)}
              aria-expanded={acctOpen}
              aria-haspopup="listbox"
            >
              <DoctorAvatar
                name={selectedDoctor.fullName}
                photoUrl={selectedDoctor.photoUrl}
                color={doctorColor(selectedIndex)}
                className="cd-acct-av"
              />
              <span className="cd-acct-name">{selectedDoctor.fullName}</span>
              <svg
                className={`cd-acct-chev${acctOpen ? ' open' : ''}`}
                viewBox="0 0 24 24"
                aria-hidden
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {acctOpen && (
              <div className="cd-acct-dropdown" role="listbox">
                {doctorList.map((d, i) => {
                  const selected = d.id === selectedDoctor.id;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`cd-acct-opt${selected ? ' selected' : ''}`}
                      onClick={() => {
                        onSelectDoctor(d.id);
                        setAcctOpen(false);
                      }}
                    >
                      <DoctorAvatar
                        name={d.fullName}
                        photoUrl={d.photoUrl}
                        color={doctorColor(i)}
                        className="cd-ao-av"
                      />
                      <span className="cd-ao-info">
                        <span className="cd-ao-name">{d.fullName}</span>
                        <span className="cd-ao-role">{doctorRole(d, i)}</span>
                      </span>
                      {selected && (
                        <svg className="cd-ao-check" viewBox="0 0 24 24" aria-hidden>
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="cd-chat-hd-sub">No clinic mentors linked yet</div>
        )}

        <div className="cd-chat-hd-sub" style={{ marginTop: 8 }}>
          {selectedDoctor
            ? `${unread > 0 ? `${unread} waiting` : 'Inbox clear'} · this doctor`
            : unread > 0
              ? `${unread} waiting`
              : 'WhatsApp-style mentor inbox'}
        </div>
      </div>
      )}
      <div className={`cd-chat-body${threadOpen ? ' is-thread' : stacked ? ' is-list' : ''}`}>
        {loading ? (
          <div className="cd-chat-empty" style={{ width: '100%' }}>
            <BrandLoader size="sm" />
          </div>
        ) : threads.length === 0 ? (
          <div className="cd-chat-empty" style={{ width: '100%' }}>
            <div>
              <strong>
                {selectedDoctor
                  ? `No messages for ${selectedDoctor.fullName}`
                  : 'No student messages yet'}
              </strong>
              <span>
                {selectedDoctor
                  ? 'Pick another doctor above, or wait for new student questions.'
                  : 'Mentor questions from students will appear here.'}
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className="cd-conv-list">
              {threads.map(t => (
                <button
                  key={t.key}
                  type="button"
                  className={`cd-conv-item${active?.key === t.key ? ' active' : ''}`}
                  onClick={() => onSelect(t.key)}
                >
                  <div className="cd-c-av">
                    {initials(t.studentName)}
                    {t.unread && <div className="cd-unread-dot" />}
                  </div>
                  <div className="cd-c-copy">
                    <div className="cd-c-name">{stacked ? t.studentName : shortName(t.studentName)}</div>
                    <div className="cd-c-prev">
                      {t.pendingCount > 0 ? `${t.pendingCount} open` : t.preview}
                    </div>
                    <div className="cd-c-time">{fmtTime(t.lastAt)}</div>
                  </div>
                </button>
              ))}
            </div>
            <div className="cd-active-chat">
              {active ? (
                <>
                  <div className="cd-chat-bar">
                    {stacked && (
                      <button
                        type="button"
                        className="cd-chat-back"
                        onClick={() => onSelect(null)}
                        aria-label="Back to chats"
                      >
                        <svg viewBox="0 0 24 24" aria-hidden>
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                      </button>
                    )}
                    <div className="cd-chat-bar-av">{initials(active.studentName)}</div>
                    <div>
                      <div className="cd-chat-bar-name">{active.studentName}</div>
                      <div className="cd-chat-bar-info">
                        Asking {active.doctorName}
                        {active.pendingCount > 0
                          ? ` · ${active.pendingCount} unanswered`
                          : ` · ${active.items.length} question${active.items.length === 1 ? '' : 's'}`}
                      </div>
                    </div>
                  </div>
                  <div className="cd-chat-msgs">
                    {active.items.map((c, index) => {
                      const isPending = c.status === 'pending' && !c.answer;
                      const draft = drafts[c.id] ?? '';
                      return (
                        <div key={c.id} className="cd-qa-block">
                          <div className="cd-qa-meta">
                            <span>Question {index + 1}</span>
                            <span>To {c.doctorName || active.doctorName}</span>
                          </div>
                          <div className="cd-msg cd-msg-in">
                            {c.question}
                            <span className="cd-msg-time">{fmtTime(c.createdAt)}</span>
                          </div>
                          {c.answer ? (
                            <div className="cd-msg cd-msg-out">
                              <div className="cd-msg-label">Reply · {c.doctorName || active.doctorName}</div>
                              {c.answer}
                              <span className="cd-msg-time">
                                {c.answeredAt ? fmtTime(c.answeredAt) : 'Sent'}
                              </span>
                            </div>
                          ) : isPending ? (
                            <div className="cd-qa-reply">
                              <div className="cd-qa-reply-label">
                                Reply as {c.doctorName || active.doctorName}
                                {c.responseDeadline
                                  ? ` · due ${new Date(c.responseDeadline).toLocaleDateString('en-GB')}`
                                  : ''}
                              </div>
                              <div className="cd-chat-input-row cd-chat-input-row--inline">
                                <input
                                  className="cd-chat-input"
                                  type="text"
                                  placeholder="Type a reply to this question…"
                                  value={draft}
                                  onChange={e => setDraft(c.id, e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' && draft.trim() && !submitting) {
                                      void submit(c.id);
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  className="cd-send-btn"
                                  disabled={!draft.trim() || submitting}
                                  onClick={() => void submit(c.id)}
                                  aria-label="Send reply"
                                >
                                  <svg className="cd-ico" viewBox="0 0 24 24">
                                    <line x1="22" y1="2" x2="11" y2="13" />
                                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="cd-qa-waiting">No reply yet</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="cd-chat-empty">
                  <div>
                    <strong>Select a conversation</strong>
                    <span>Pick a student thread on the left.</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
